import { coordinatorLabel } from '@reditelji/shared';
import { useMemo } from 'react';
import { type CoordinatorName, useCoordinatorNames } from '../api/coordinators';

/** Resolves a coordinator's id to what the app calls them; see `labelFrom`. */
export type CoordinatorLabelOf = (id: string | null | undefined, snapshot?: string | null, fallback?: string) => string;

/**
 * A coordinator by their name as it is now, not as it was when the row was
 * written: somebody who set their name today reads by it in last week's
 * Dnevnik too. The snapshot a row kept is the fallback, for an id the names
 * list does not know (yet, or any more) or a login that still has no name.
 * `fallback` is the last resort: the role word, or '' where a row nobody wrote
 * should say nothing at all.
 */
export function labelFrom(
  names: ReadonlyMap<string, string>,
  id: string | null | undefined,
  snapshot?: string | null,
  fallback = 'koordinator',
): string {
  const current = id ? names.get(id) : undefined;
  return coordinatorLabel(current === undefined ? null : { name: current }, snapshot?.trim() || fallback);
}

/** " · Nina Kranjc", or nothing for a label that is empty. */
export const bySuffix = (label: string): string => (label ? ` · ${label}` : '');

export function namesById(rows: readonly CoordinatorName[] | undefined): Map<string, string> {
  return new Map((rows ?? []).map((r) => [r.id, r.name]));
}

export function useCoordinatorLabel(): CoordinatorLabelOf {
  const names = useCoordinatorNames().data;
  return useMemo(() => {
    const byId = namesById(names);
    return (id, snapshot, fallback) => labelFrom(byId, id, snapshot, fallback);
  }, [names]);
}

/**
 * Who did it, for a Dnevnik row. Only a coordinator is looked up by id: a
 * volunteer acting from their page, the mailbox and the master login are
 * written down by the name they had, and nothing else holds a better one.
 */
export function actorLabel(
  entry: { actorType: string; actorId: string | null; actorName: string },
  label: CoordinatorLabelOf,
): string {
  if (entry.actorType === 'coordinator') return label(entry.actorId, entry.actorName);
  return entry.actorName || 'Sistem';
}
